import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBabyAge } from '@/hooks/useBabyAge';
import { useBabyStore } from '@/stores/useBabyStore';
import type { Baby } from '@/types';
import { BabyAvatar } from './BabyAvatar';
import { isGerman } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';

const MAX_BABIES = 3;

export function BabyAgeChip({ baby }: { baby: Baby }) {
  const ageLabel = useBabyAge(baby.date_of_birth);
  const babies = useBabyStore((s) => s.babies);
  const setActive = useBabyStore((s) => s.setActive);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Single baby — old behavior, no dropdown affordance.
  if (babies.length <= 1) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 border border-primary-100 pl-1 pr-3 py-0.5 text-[13px] text-primary-700">
        <BabyAvatar baby={baby} size={22} />
        <span className="font-medium">{baby.name}</span>
        <span className="text-primary-500">·</span>
        <span>{ageLabel}</span>
      </div>
    );
  }

  // 2+ babies — tappable, opens a switcher menu.
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full bg-primary-50 border border-primary-100 hover:border-primary-300 pl-1 pr-3 py-0.5 text-[13px] text-primary-700 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${baby.name} · ${ageLabel}`}
      >
        <BabyAvatar baby={baby} size={22} />
        <span className="font-medium">{baby.name}</span>
        <span className="text-primary-500">·</span>
        <span>{ageLabel}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={'opacity-60 transition-transform ' + (open ? 'rotate-180' : '')}
          aria-hidden
        >
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-1.5 w-56 rounded-xl bg-white shadow-lg border border-primary-100 z-30 overflow-hidden"
        >
          {babies.map((b) => (
            <BabyMenuItem
              key={b.id}
              baby={b}
              isActive={b.id === baby.id}
              onSelect={() => {
                setActive(b.id);
                setOpen(false);
              }}
            />
          ))}
          <div className="border-t border-primary-50">
            <button
              type="button"
              disabled={babies.length >= MAX_BABIES}
              onClick={() => {
                setOpen(false);
                navigate('/babies/new');
              }}
              className="w-full text-left px-3 py-2.5 text-sm text-primary-700 hover:bg-primary-50 disabled:text-slate-muted disabled:hover:bg-transparent disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {babies.length < MAX_BABIES && <Icon.Plus size={14} />}
              {babies.length >= MAX_BABIES
                ? (isGerman() ? 'Maximal 3 Babys' : 'Up to 3 babies')
                : (isGerman() ? 'Weiteres Baby hinzufügen' : 'Add another baby')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BabyMenuItem({
  baby,
  isActive,
  onSelect,
}: {
  baby: Baby;
  isActive: boolean;
  onSelect: () => void;
}) {
  const ageLabel = useBabyAge(baby.date_of_birth);
  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      onClick={onSelect}
      className={
        'w-full text-left px-3 py-2.5 text-sm transition-colors flex items-center gap-2.5 ' +
        (isActive
          ? 'bg-primary-50 text-primary-800 font-medium'
          : 'text-slate hover:bg-primary-50/60')
      }
    >
      <BabyAvatar baby={baby} size={28} />
      <span className="truncate flex-1 text-left">
        {baby.name}
        <span className="text-slate-muted font-normal"> · {ageLabel}</span>
      </span>
      {isActive && (
        <svg width="14" height="14" viewBox="0 0 14 14" className="text-primary shrink-0">
          <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}
